import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { adminMt5Stats, adminMt5Accounts, adminMt5Action } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const STAT_CARDS = [
  ["total", "Total"], ["pending", "Pending"], ["approved", "Approved"], ["connected", "Connected"],
  ["paused", "Paused"], ["rejected", "Rejected"], ["disconnected", "Disconnected"], ["open_trades", "Open trades"],
];

const STATUS_FILTERS = ["", "pending", "approved", "paused", "rejected", "disconnected", "disabled"];

const ACTIONS_BY_STATUS = {
  pending:      ["approve", "reject"],
  approved:     ["pause", "disconnect", "close-all", "disable"],
  paused:       ["resume", "disconnect", "disable"],
  rejected:     ["approve"],
  disconnected: ["approve"],
  disabled:     ["approve"],
};

const ACTION_STYLE = {
  approve: "bg-bull/20 text-bull border-bull/40",
  resume:  "bg-bull/20 text-bull border-bull/40",
  reject:  "bg-bear/20 text-bear border-bear/40",
  disable: "bg-bear/20 text-bear border-bear/40",
  "close-all": "bg-bear/20 text-bear border-bear/40",
  pause:   "bg-amber-400/20 text-amber-300 border-amber-400/40",
  disconnect: "bg-white/5 text-zinc-300 border-white/15",
};

export default function Mt5Tab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState("");

  const { data: stats } = useQuery({ queryKey: ["admin-mt5-stats"], queryFn: adminMt5Stats, refetchInterval: 10000 });
  const { data, isLoading } = useQuery({
    queryKey: ["admin-mt5-accounts", filter],
    queryFn: () => adminMt5Accounts(filter ? { status: filter } : {}),
    refetchInterval: 10000,
  });

  const actMut = useMutation({
    mutationFn: ({ userId, action }) => adminMt5Action(userId, action),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-mt5-accounts"] });
      qc.invalidateQueries({ queryKey: ["admin-mt5-stats"] });
      toast({ title: `Action: ${vars.action}` });
    },
    onError: () => toast({ title: "Action failed", variant: "destructive" }),
  });

  return (
    <div className="space-y-5" data-testid="admin-mt5-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {STAT_CARDS.map(([k, label]) => (
          <div key={k} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3" data-testid={`mt5-stat-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
            <div className="text-xl font-bold text-white">{stats?.[k] ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            data-testid={`mt5-filter-${s || "all"}`}
            className={`px-3 py-1 rounded-full text-[11px] ${filter === s ? "bg-amber-400/90 text-ink-900" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            {s || "all"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Loader2 className="size-5 animate-spin text-white/50" />
      ) : (data?.accounts || []).length === 0 ? (
        <div className="text-sm text-white/50 py-6">No MT5 accounts in this filter.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="admin-mt5-table">
            <thead className="text-white/40 text-left">
              <tr>
                <th className="py-2">User</th><th>Broker</th><th>Login</th><th>Status</th>
                <th>Auto</th><th>Open</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.id} className="border-t border-white/5" data-testid={`mt5-row-${a.user_id}`}>
                  <td className="py-2.5">{a.user_email}</td>
                  <td>{a.broker_name}</td>
                  <td className="mono">{a.login}</td>
                  <td><StatusChip status={a.status} /></td>
                  <td>{a.settings?.autotrading ? "ON" : "off"}</td>
                  <td>{a.open_trades}</td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {(ACTIONS_BY_STATUS[a.status] || []).map((act) => (
                        <button key={act}
                          data-testid={`mt5-action-${act}-${a.user_id}`}
                          disabled={actMut.isPending}
                          onClick={() => actMut.mutate({ userId: a.user_id, action: act })}
                          className={`px-2 py-1 rounded border text-[10px] uppercase tracking-wide ${ACTION_STYLE[act] || "border-white/15"}`}>
                          {act}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    approved: "text-bull border-bull/40", connected: "text-bull border-bull/40",
    pending: "text-amber-300 border-amber-400/40", paused: "text-amber-300 border-amber-400/40",
    rejected: "text-bear border-bear/40", disabled: "text-bear border-bear/40",
    disconnected: "text-zinc-400 border-white/15",
  };
  return <span className={`px-2 py-0.5 rounded-full border text-[10px] ${map[status] || "border-white/15"}`}>{status}</span>;
}
